import React from "react";
import { IoIosSearch, IoIosClose, IoIosArrowDown } from "react-icons/io";
import { useWallet, useWalletAmount } from "../../api";
import WalletLoader from "../../loader/WalletLoader";
import WalletCardSelect from "./WalletCardSelect";
import {
  classifyWallet,
  formatRelativeDate,
  mergeWalletStats,
  INACTIVE_DAYS,
} from "./walletGrouping";
import "./wallet.css";

// Selector mode only — picks a wallet for the transaction filter chip.
// Mirrors the management view's active/inactive grouping so user sees
// only relevant wallets first.
export default function WalletList(props) {
  const { wallets, isLoading } = useWallet();
  const { walletAmounts } = useWalletAmount();
  const rawCurrent = props.match.params.current;
  const currentId =
    rawCurrent && rawCurrent !== "undefined" ? rawCurrent : null;
  const KEY = props.match.params.key;
  const all = props.match.params.all;
  const includeAllOption = all === "all";

  const [search, setSearch] = React.useState("");
  const [showInactive, setShowInactive] = React.useState(false);

  const ALL_WALLET = {
    id: "0",
    name: "Semua Wallet",
    description: "Tampilkan transaksi dari seluruh wallet",
  };

  const selectWallet = (wallet) => {
    localStorage.setItem(KEY, JSON.stringify(wallet));
    window.history.back();
  };

  const merged = React.useMemo(
    () => mergeWalletStats(wallets, walletAmounts),
    [wallets, walletAmounts]
  );

  const { active, inactive } = React.useMemo(() => {
    const a = [];
    const i = [];
    for (const w of merged) {
      if (classifyWallet(w) === "inactive") i.push(w);
      else a.push(w);
    }
    i.sort((x, y) => {
      const dx = x.lastUsed ? new Date(x.lastUsed).getTime() : 0;
      const dy = y.lastUsed ? new Date(y.lastUsed).getTime() : 0;
      return dy - dx;
    });
    return { active: a, inactive: i };
  }, [merged]);

  const applySearch = (arr) => {
    const q = search.trim().toLowerCase();
    if (!q) return arr;
    return arr.filter(
      (w) =>
        w.name.toLowerCase().includes(q) ||
        (w.description || "").toLowerCase().includes(q)
    );
  };

  const filteredActive = applySearch(active);
  const filteredInactive = applySearch(inactive);
  const inactiveExpanded =
    showInactive || (search.trim() && filteredInactive.length > 0);

  if (isLoading) {
    return (
      <div className="main-padding">
        <WalletLoader />
      </div>
    );
  }

  return (
    <div className="main-padding">
      <div className="wallet-select-hint">
        Pilih wallet untuk filter daftar transaksi.
      </div>

      {(active.length > 0 || inactive.length > 0 || includeAllOption) && (
        <div className="wallet-search">
          <span className="wallet-search__icon">
            <IoIosSearch />
          </span>
          <input
            type="search"
            className="wallet-search__input"
            placeholder="Cari wallet…"
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="wallet-search__clear"
              onClick={() => setSearch("")}
              aria-label="Hapus pencarian"
            >
              <IoIosClose />
            </button>
          )}
        </div>
      )}

      {/* "Semua Wallet" sits before any grouping when applicable */}
      {includeAllOption && !search && (
        <ul className="wallet-list">
          <WalletCardSelect
            data={ALL_WALLET}
            selected={currentId === "0"}
            onClick={() => selectWallet(ALL_WALLET)}
          />
        </ul>
      )}

      {filteredActive.length > 0 && (
        <>
          <div className="wallet-group-title">
            AKTIF
            <span className="wallet-group-count">{filteredActive.length}</span>
          </div>
          <ul className="wallet-list">
            {filteredActive.map((w) => (
              <WalletCardSelect
                key={w.id}
                data={w}
                selected={w.id === currentId}
                onClick={() => selectWallet(w)}
              />
            ))}
          </ul>
        </>
      )}

      {inactive.length > 0 && (
        <>
          <button
            type="button"
            className={`wallet-group-toggle ${
              inactiveExpanded ? "wallet-group-toggle--open" : ""
            }`}
            onClick={() => setShowInactive((v) => !v)}
            aria-expanded={inactiveExpanded}
          >
            <span className="wallet-group-toggle__label">
              LIHAT SEMUA WALLET
              <span className="wallet-group-count">
                +{filteredInactive.length}
                {search && filteredInactive.length !== inactive.length
                  ? ` dari ${inactive.length}`
                  : ""}
              </span>
            </span>
            <span className="wallet-group-toggle__hint">
              Tidak ada transaksi {INACTIVE_DAYS} hari terakhir
            </span>
            <IoIosArrowDown className="wallet-group-toggle__chev" />
          </button>
          {inactiveExpanded && filteredInactive.length > 0 && (
            <ul className="wallet-list wallet-list--inactive">
              {filteredInactive.map((w) => (
                <WalletCardSelect
                  key={w.id}
                  data={w}
                  selected={w.id === currentId}
                  inactive
                  lastUsedLabel={formatRelativeDate(w.lastUsed)}
                  onClick={() => selectWallet(w)}
                />
              ))}
            </ul>
          )}
        </>
      )}

      {filteredActive.length === 0 &&
        filteredInactive.length === 0 &&
        !includeAllOption && (
          <div className="wallet-placeholder">
            {search ? `Tidak ada wallet yang cocok dengan "${search}".` : "Belum ada wallet."}
          </div>
        )}
    </div>
  );
}
